import { createBrowserRouter, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import { AuthenticatedRoute } from "../guards/AuthenticatedRoute";
import { AdminRoute } from "../guards/AdminRoute";
import { RequireCapability } from "../guards/RequireCapability";
import { UserPortalLayout } from "@/layouts/UserPortalLayout/UserPortalLayout";
import { AdminPortalLayout } from "@/layouts/AdminPortalLayout/AdminPortalLayout";
import { LoadingState } from "@/components/carmel/LoadingState";
import { CAPABILITIES } from "@carmel/shared";

// All pages lazy-loaded for optimal code splitting
const LoginPage = lazy(() => import("@/features/auth/pages/LoginPage").then((m) => ({ default: m.LoginPage })));
const DashboardPage = lazy(() => import("@/features/dashboard/pages/DashboardPage").then((m) => ({ default: m.DashboardPage })));
const ForgotPasswordPage = lazy(() => import("@/features/auth/pages/ForgotPasswordPage").then((m) => ({ default: m.ForgotPasswordPage })));
const TasksPage = lazy(() => import("@/features/tasks/pages/TasksPage").then((m) => ({ default: m.TasksPage })));
const TaskDetailPage = lazy(() => import("@/features/tasks/pages/TaskDetailPage").then((m) => ({ default: m.TaskDetailPage })));
const TaskEditPage = lazy(() => import("@/features/tasks/pages/TaskEditPage").then((m) => ({ default: m.TaskEditPage })));
const TaskRequestsPage = lazy(() => import("@/features/taskRequests/pages/TaskRequestsPage").then((m) => ({ default: m.TaskRequestsPage })));
const NotificationsPage = lazy(() => import("@/features/notifications/pages/NotificationsPage").then((m) => ({ default: m.NotificationsPage })));
const ApprovalsPage = lazy(() => import("@/features/approvals/pages/ApprovalsPage").then((m) => ({ default: m.ApprovalsPage })));
const ReportsPage = lazy(() => import("@/features/reports/pages/ReportsPage").then((m) => ({ default: m.ReportsPage })));
const MyTeamPage = lazy(() => import("@/features/team/pages/MyTeamPage").then((m) => ({ default: m.MyTeamPage })));
const TeamMemberPage = lazy(() => import("@/features/team/pages/TeamMemberPage").then((m) => ({ default: m.TeamMemberPage })));
const SettingsPage = lazy(() => import("@/features/settings/pages/SettingsPage").then((m) => ({ default: m.SettingsPage })));
const UsersPage = lazy(() => import("@/features/users/pages/UsersPage").then((m) => ({ default: m.UsersPage })));
const UserDetailPage = lazy(() => import("@/features/users/pages/UserDetailPage").then((m) => ({ default: m.UserDetailPage })));
const RolesPage = lazy(() => import("@/features/roles/pages/RolesPage").then((m) => ({ default: m.RolesPage })));
const DepartmentsPage = lazy(() => import("@/features/organisation/pages/DepartmentsPage").then((m) => ({ default: m.DepartmentsPage })));
const TeamsPage = lazy(() => import("@/features/organisation/pages/TeamsPage").then((m) => ({ default: m.TeamsPage })));
const DepartmentDashboardPage = lazy(() => import("@/features/department-dashboard/pages/DepartmentDashboardPage").then((m) => ({ default: m.DepartmentDashboardPage })));
const OversightPage = lazy(() => import("@/features/oversight/pages/OversightPage").then((m) => ({ default: m.OversightPage })));
const OversightOverviewPage = lazy(() => import("@/features/oversight/pages/OversightOverviewPage").then((m) => ({ default: m.OversightOverviewPage })));
const InterventionsPage = lazy(() => import("@/features/oversight/pages/InterventionsPage").then((m) => ({ default: m.InterventionsPage })));
const UsersPerformancePage = lazy(() => import("@/features/oversight/pages/UsersPerformancePage").then((m) => ({ default: m.UsersPerformancePage })));
const OversightReportsPage = lazy(() => import("@/features/oversight/pages/ReportsPage").then((m) => ({ default: m.ReportsPage })));

function SuspenseWrapper({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<LoadingState />}>{children}</Suspense>;
}

export const router = createBrowserRouter([
  // Public routes
  {
    path: "/login",
    element: <SuspenseWrapper><LoginPage /></SuspenseWrapper>,
  },
  {
    path: "/forgot-password",
    element: <SuspenseWrapper><ForgotPasswordPage /></SuspenseWrapper>,
  },

  // Protected user routes
  {
    element: <AuthenticatedRoute />,
    children: [
      {
        path: "/",
        element: <UserPortalLayout />,
        children: [
          { index: true, element: <SuspenseWrapper><DashboardPage /></SuspenseWrapper> },
          { path: "home", element: <SuspenseWrapper><DashboardPage /></SuspenseWrapper> },
          { path: "tasks", element: <SuspenseWrapper><TasksPage /></SuspenseWrapper> },
          { path: "tasks/new", element: <SuspenseWrapper><TasksPage /></SuspenseWrapper> },
          { path: "tasks/:taskId", element: <SuspenseWrapper><TaskDetailPage /></SuspenseWrapper> },
          { path: "tasks/:taskId/edit", element: <SuspenseWrapper><TaskEditPage /></SuspenseWrapper> },
          { path: "task-requests", element: <SuspenseWrapper><TaskRequestsPage /></SuspenseWrapper> },
          { path: "notifications", element: <SuspenseWrapper><NotificationsPage /></SuspenseWrapper> },
          { path: "approvals", element: <SuspenseWrapper><ApprovalsPage /></SuspenseWrapper> },
          { path: "reports", element: <SuspenseWrapper><ReportsPage /></SuspenseWrapper> },
          { path: "my-team", element: <SuspenseWrapper><MyTeamPage /></SuspenseWrapper> },
          { path: "my-team/:memberId", element: <SuspenseWrapper><TeamMemberPage /></SuspenseWrapper> },
          { path: "settings", element: <SuspenseWrapper><SettingsPage /></SuspenseWrapper> },
          { path: "calendar", element: <SuspenseWrapper><TasksPage /></SuspenseWrapper> },
          { path: "more", element: <SuspenseWrapper><SettingsPage /></SuspenseWrapper> },
        ],
      },
    ],
  },

  // Protected department dashboard routes (department heads)
  {
    element: <AuthenticatedRoute />,
    children: [
      {
        element: (
          <RequireCapability
            anyCapability={[CAPABILITIES.DASHBOARD_VIEW_DEPARTMENT, CAPABILITIES.DASHBOARD_VIEW_ORGANISATION]}
            redirectTo="/home"
          />
        ),
        children: [
          {
            path: "/department-dashboard",
            element: <UserPortalLayout />,
            children: [
              { index: true, element: <SuspenseWrapper><DepartmentDashboardPage /></SuspenseWrapper> },
            ],
          },
        ],
      },
    ],
  },

  // Protected oversight routes (Admin/Superadmin)
  {
    element: <AuthenticatedRoute />,
    children: [
      {
        element: (
          <RequireCapability
            capabilities={[CAPABILITIES.DASHBOARD_VIEW_ORGANISATION]}
            redirectTo="/home"
          />
        ),
        children: [
          {
            path: "/oversight",
            element: <AdminPortalLayout />,
            children: [
              { index: true, element: <SuspenseWrapper><OversightOverviewPage /></SuspenseWrapper> },
              { path: "interventions", element: <SuspenseWrapper><InterventionsPage /></SuspenseWrapper> },
              { path: "users", element: <SuspenseWrapper><UsersPerformancePage /></SuspenseWrapper> },
              { path: "users/:userId", element: <SuspenseWrapper><UsersPerformancePage /></SuspenseWrapper> },
              { path: "reports", element: <SuspenseWrapper><OversightReportsPage /></SuspenseWrapper> },
            ],
          },
        ],
      },
    ],
  },

  // Protected admin routes
  {
    element: <AuthenticatedRoute />,
    children: [
      {
        element: <AdminRoute />,
        children: [
          {
            path: "/admin",
            element: <AdminPortalLayout />,
            children: [
              { index: true, element: <SuspenseWrapper><UsersPage /></SuspenseWrapper> },
              { path: "users", element: <SuspenseWrapper><UsersPage /></SuspenseWrapper> },
              { path: "users/:userId", element: <SuspenseWrapper><UserDetailPage /></SuspenseWrapper> },
              { path: "roles", element: <SuspenseWrapper><RolesPage /></SuspenseWrapper> },
              { path: "departments", element: <SuspenseWrapper><DepartmentsPage /></SuspenseWrapper> },
              { path: "teams", element: <SuspenseWrapper><TeamsPage /></SuspenseWrapper> },
            ],
          },
        ],
      },
    ],
  },

  // Catch all
  { path: "*", element: <Navigate to="/home" replace /> },
]);
